import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Users, Clock, Coffee, AlertTriangle, CheckCircle, ClipboardList, Bell, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { devError } from '@/lib/logger';
import AdjustmentDialog from '@/components/AdjustmentDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [presence, setPresence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adjPrefill, setAdjPrefill] = useState(null);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(res.data);
      if (res.data.length > 0) {
        setSelectedLocation(res.data[0].location_id);
      }
    } catch (err) { devError("dashboard", err);
      toast.error('Erro ao carregar locais');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      const res = await axios.get(`${API}/dashboard/stats?location_id=${selectedLocation}`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      devError('fetchStats', err);
    }
  }, [selectedLocation]);

  const fetchPresence = useCallback(async () => {
    if (!selectedLocation) return;
    try {
      const res = await axios.get(`${API}/time-records/live/${selectedLocation}`, { withCredentials: true });
      setPresence(res.data);
    } catch (err) {
      devError('fetchPresence', err);
    }
  }, [selectedLocation]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    if (selectedLocation) {
      fetchStats();
      fetchPresence();
      const interval = setInterval(fetchPresence, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedLocation, fetchStats, fetchPresence]);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#111111] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  if (locations.length === 0) {
    return (
      <AdminLayout title="Dashboard">
        <div className="text-center py-16 border border-[#E5E7EB]">
          <p className="text-[#6B7280] mb-4">Nenhum local cadastrado</p>
          <Button onClick={() => window.location.href = '/admin/locations'} data-testid="add-location-btn">
            Adicionar Local
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      {/* Location Selector */}
      <div className="flex items-center gap-4 mb-6">
        <label className="text-xs uppercase tracking-widest text-[#6B7280]">Local:</label>
        <select
          data-testid="location-selector"
          value={selectedLocation || ''}
          onChange={(e) => setSelectedLocation(e.target.value)}
          className="border border-[#E5E7EB] px-4 py-2 bg-white text-sm"
        >
          {locations.map(loc => (
            <option key={loc.location_id} value={loc.location_id}>
              {loc.name}
            </option>
          ))}
        </select>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={<Users className="w-6 h-6" />}
            value={stats.total_employees}
            label="Total Funcionários"
            testId="stat-total"
          />
          <StatCard
            icon={<Clock className="w-6 h-6" />}
            value={stats.working}
            label="Trabalhando"
            color="present"
            testId="stat-working"
          />
          <StatCard
            icon={<Coffee className="w-6 h-6" />}
            value={stats.on_break}
            label="Em Pausa"
            color="break"
            testId="stat-break"
          />
          <StatCard
            icon={<AlertTriangle className="w-6 h-6" />}
            value={stats.late}
            label="Atrasados"
            color="late"
            testId="stat-late"
          />
          <StatCard
            icon={<CheckCircle className="w-6 h-6" />}
            value={stats.completed}
            label="Concluídos"
            testId="stat-completed"
          />
          <StatCard
            icon={<Users className="w-6 h-6" />}
            value={stats.absent}
            label="Ausentes"
            color="inactive"
            testId="stat-absent"
          />
          <StatCard
            icon={<ClipboardList className="w-6 h-6" />}
            value={stats.pending_requests}
            label="Solicitações"
            testId="stat-requests"
          />
          <StatCard
            icon={<Bell className="w-6 h-6" />}
            value={stats.unread_alerts}
            label="Alertas"
            color={stats.unread_alerts > 0 ? 'late' : undefined}
            testId="stat-alerts"
          />
        </div>
      )}

      {/* Live Presence */}
      <div className="border border-[#E5E7EB]">
        <div className="bg-[#F3F4F6] px-4 py-3 border-b border-[#E5E7EB] flex items-center justify-between">
          <h2 className="text-xs uppercase tracking-widest font-semibold">Presença ao Vivo</h2>
          <Button variant="ghost" size="sm" onClick={fetchPresence} data-testid="refresh-presence-btn">
            Atualizar
          </Button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-0">
          {presence.map(item => (
            <PresenceCard
              key={item.employee.employee_id}
              data={item}
              onAdjust={(payload) => setAdjPrefill(payload)}
            />
          ))}
        </div>
        
        {presence.length === 0 && (
          <div className="p-8 text-center text-[#6B7280]">
            Nenhum funcionário cadastrado neste local
          </div>
        )}
      </div>

      <AdjustmentDialog
        open={!!adjPrefill}
        onOpenChange={(o) => !o && setAdjPrefill(null)}
        prefill={adjPrefill || {}}
        onCreated={() => { setAdjPrefill(null); fetchPresence(); }}
      />
    </AdminLayout>
  );
}

function StatCard({ icon, value, label, color, testId }) {
  const colorClasses = {
    present: 'text-[#059669]',
    break: 'text-[#F59E0B]',
    late: 'text-[#DC2626]',
    inactive: 'text-[#9CA3AF]'
  };

  return (
    <div className="stat-card" data-testid={testId}>
      <div className={`${colorClasses[color] || 'text-[#111111]'}`}>
        {icon}
      </div>
      <div className={`stat-card-value ${colorClasses[color] || ''}`}>
        {value}
      </div>
      <div className="stat-card-label">{label}</div>
    </div>
  );
}

function PresenceCard({ data, onAdjust }) {
  const { employee, record, current_status } = data;
  
  const statusLabels = {
    not_started: 'Não Iniciado',
    working: 'Trabalhando',
    on_break: 'Em Pausa',
    completed: 'Concluído'
  };

  const statusColors = {
    not_started: 'bg-[#9CA3AF]',
    working: 'bg-[#059669]',
    on_break: 'bg-[#F59E0B]',
    completed: 'bg-[#111111]'
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (err) { devError("dashboard", err);
      return '-';
    }
  };

  const canAdjust = !!record && (current_status === 'completed' || current_status === 'working' || current_status === 'on_break');

  const openAdjust = () => {
    if (!record) return;
    const date = record.date || (record.clock_in ? record.clock_in.slice(0, 10) : new Date().toISOString().slice(0, 10));
    let originalTimeOut = '';
    if (record.clock_out) {
      try {
        const d = new Date(record.clock_out);
        originalTimeOut = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      } catch (err) { devError('presence-time', err); }
    }
    onAdjust({
      employee_id: employee.employee_id,
      employee_name: employee.name,
      location_id: record.location_id,
      date,
      original_time_out: originalTimeOut,
    });
  };

  return (
    <div 
      className="presence-card border-r border-b border-[#E5E7EB]"
      data-testid={`presence-card-${employee.employee_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-sm">{employee.name}</h3>
          <p className="text-xs text-[#6B7280]">{employee.email}</p>
        </div>
        <span className={`badge ${statusColors[current_status]} text-white`}>
          {statusLabels[current_status]}
        </span>
      </div>
      
      {record && (
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-[#6B7280]">Entrada:</span>
            <span className="ml-1 mono">{formatTime(record.clock_in)}</span>
          </div>
          <div>
            <span className="text-[#6B7280]">Pausa:</span>
            <span className="ml-1 mono">{formatTime(record.break_start)}</span>
          </div>
          <div>
            <span className="text-[#6B7280]">Retorno:</span>
            <span className="ml-1 mono">{formatTime(record.break_end)}</span>
          </div>
          <div>
            <span className="text-[#6B7280]">Saída:</span>
            <span
              className={`ml-1 mono ${record?.is_auto_closed ? 'text-[#DC2626] font-bold' : ''}`}
              title={record?.is_auto_closed ? 'Fechamento compulsório (auto ou manual pelo gestor)' : ''}
              data-testid={record?.is_auto_closed ? `clock-out-autoclosed-${employee.employee_id}` : undefined}
            >
              {formatTime(record.clock_out)}
              {record?.is_auto_closed ? ' *' : ''}
            </span>
          </div>
        </div>
      )}

      {record?.is_auto_closed && (
        <div className="mt-1 text-[10px] text-[#DC2626] uppercase tracking-wider">
          * Saída registada por fechamento compulsório
        </div>
      )}
      
      {record?.is_late && (
        <div className="mt-2 text-xs text-[#DC2626] font-semibold uppercase">
          Atrasado
        </div>
      )}

      {canAdjust && (
        <Button
          variant="ghost"
          size="sm"
          onClick={openAdjust}
          className="mt-3 w-full justify-center text-xs h-8 border border-[#E5E7EB] hover:bg-[#0029FF] hover:text-white hover:border-[#0029FF]"
          data-testid={`presence-adjust-${employee.employee_id}`}
        >
          <Edit3 className="w-3 h-3 mr-1" />
          Ajustar saída
        </Button>
      )}
    </div>
  );
}
